import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Check, Ruler } from 'lucide-react';
import { Button, Input, Select } from '@easymoving/ui';
import type { DimensionUnit, Item } from '@easymoving/types';
import { useBackend } from '../../lib/backend/context';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useUnits } from '../../lib/units/useUnits';
import { unitLabel, unitOptions } from '../../lib/taxonomy/options';

const num = (s: string): number | undefined => {
  const n = Number(s);
  return s.trim() !== '' && Number.isFinite(n) ? n : undefined;
};
const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Dimensions + weight on the item detail page, editable in place. Reads/writes
 * straight through the backend; weight is shown/entered in the user's preferred
 * unit and stored in kg.
 */
export function InlineMeasurements({ item }: { item: Item }) {
  const { t } = useTranslation();
  const backend = useBackend();
  const { canEdit } = useWorkspace();
  const units = useUnits();

  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [l, setL] = useState(item.length?.toString() ?? '');
  const [w, setW] = useState(item.width?.toString() ?? '');
  const [h, setH] = useState(item.height?.toString() ?? '');
  const [unit, setUnit] = useState<DimensionUnit>(item.dimensionUnit ?? units.defaultDimUnit);
  const [weight, setWeight] = useState(
    item.weight != null ? String(round2(units.weightToDisplay(item.weight))) : '',
  );

  async function save() {
    setBusy(true);
    const wv = num(weight);
    try {
      await backend.updateItem(item.id, {
        length: num(l),
        width: num(w),
        height: num(h),
        dimensionUnit: unit,
        weight: wv != null ? units.weightFromInput(wv) : undefined,
      });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  }

  const dims =
    item.length || item.width || item.height
      ? [item.length, item.width, item.height].filter(Boolean).join(' × ') +
        (item.dimensionUnit ? ` ${unitLabel(item.dimensionUnit, t)}` : '')
      : item.dimensionsText;
  const empty = !dims && item.weight == null;

  if (!editing) {
    // Empty: the whole area is one inviting, clickable panel.
    if (empty) {
      if (!canEdit) return null; // viewers: nothing to show, nothing to add
      return (
        <div className="border-t hairline pt-4">
          <h2 className="smallcaps text-graphite mb-2">{t('item.measurements')}</h2>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="group w-full flex items-center gap-3 rounded-xl border-2 border-dashed border-rule-strong hover:border-petrol hover:bg-petrol-tint/40 p-4 text-start transition-colors"
          >
            <span className="size-10 shrink-0 rounded-full bg-surface-tint text-graphite group-hover:bg-petrol group-hover:text-white flex items-center justify-center transition-colors">
              <Ruler className="size-5" />
            </span>
            <span className="min-w-0">
              <span className="block font-medium text-[14px] text-ink">{t('item.noMeasurements')}</span>
              <span className="block text-[12px] text-graphite-soft">{t('item.addMeasurementsHint')}</span>
            </span>
          </button>
        </div>
      );
    }
    return (
      <div className="border-t hairline pt-4">
        <div className="flex items-center justify-between">
          <h2 className="smallcaps text-graphite">{t('item.measurements')}</h2>
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-petrol text-[13px] font-medium inline-flex items-center gap-1"
            >
              <Pencil className="size-3.5" />
              {t('item.edit')}
            </button>
          )}
        </div>
        <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-[14px] mt-2">
          {dims && (
            <div>
              <dt className="text-graphite-soft text-[12px]">{t('item.dimensions')}</dt>
              <dd className="text-ink">{dims}</dd>
            </div>
          )}
          {item.weight != null && (
            <div>
              <dt className="text-graphite-soft text-[12px]">{t('item.weight')}</dt>
              <dd className="text-ink">{units.formatWeight(item.weight)}</dd>
            </div>
          )}
        </dl>
      </div>
    );
  }

  return (
    <div className="border-t hairline pt-4">
      <h2 className="smallcaps text-graphite mb-2">{t('item.measurements')}</h2>
      <div className="grid grid-cols-4 gap-2">
        <Input placeholder={t('item.length')} aria-label={t('item.length')} inputMode="decimal" value={l} onChange={(e) => setL(e.target.value)} />
        <Input placeholder={t('item.width')} aria-label={t('item.width')} inputMode="decimal" value={w} onChange={(e) => setW(e.target.value)} />
        <Input placeholder={t('item.height')} aria-label={t('item.height')} inputMode="decimal" value={h} onChange={(e) => setH(e.target.value)} />
        <Select ariaLabel={t('item.unit')} value={unit} options={unitOptions(t)} onValueChange={(v) => setUnit(v as DimensionUnit)} />
      </div>
      <div className="relative mt-2">
        <Input
          inputMode="decimal"
          value={weight}
          aria-label={t('item.weight')}
          onChange={(e) => setWeight(e.target.value)}
          placeholder={t('item.weight')}
          className="pe-12"
        />
        <span className="absolute top-1/2 -translate-y-1/2 end-3 text-[13px] text-graphite-soft pointer-events-none">
          {units.weightLabel}
        </span>
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={save} disabled={busy}>
          <Check className="size-4" />
          {t('item.save')}
        </Button>
        <Button variant="ghost" onClick={() => setEditing(false)}>
          {t('item.cancel')}
        </Button>
      </div>
    </div>
  );
}
